/**
 * Northstar-owned fleet / crew Gantt — one trip truth from the mission ticket.
 * Pure layout math. Desk paints; detectClashes stays the clash source.
 */
import { assignmentForMission, type TripAssignment } from "./assignment";
import {
  addDaysInTimeZone,
  coerceClockDate,
  formatCardDateInZone,
  formatHmInZone,
  formatUtcHm,
  HALIFAX_TZ,
  partsInTimeZone,
  wallTimeInTimeZone,
  zoneAbbr,
} from "./clock";
import { timeZoneForIcao } from "./airportTimeZone";
import {
  clashesForMission,
  missionStaMs,
  missionStdMs,
  type ClashKind,
  type ClashSeverity,
  type DispatchClash,
} from "./dispatchBrain";
import {
  DEFAULT_DUTY_LIMITS,
  formatDutyHours,
  type DutyLevel,
  type DutyLimits,
  type DutyView,
} from "./duty";
import { displayFlightNumber } from "./flightNumber";
import { ticketLifecycle } from "./opsBoard";
import { ticketStatusTone, type TicketStatusTone } from "./statusColor";
import { releasePackStatusOf } from "./releasePack";
import { openTripSupportCount } from "./tripSupport";
import type { AircraftSpec, GanttTimeDisplay, Mission, ReleasePackStatus } from "./types";

export type GanttRange = "day" | "week";

export type GanttBarKind = "charter" | "ferry" | "repo" | "mx";

export type GanttLaneKind = "tail" | "crew";

/** Schedule Gantt: tails vs the same tickets laid on crew. */
export type GanttLaneMode = "aircraft" | "crew";

export type GanttDutyBandKind = "duty" | "rest" | "limit" | "overrun";

export type GanttCrewRef = {
  id: string;
  name: string;
};

export function hydrateGanttLaneMode(raw: unknown): GanttLaneMode {
  return raw === "crew" ? "crew" : "aircraft";
}

export const GANTT_BAR_KIND_LABEL: Record<GanttBarKind, string> = {
  charter: "Charter",
  ferry: "Ferry",
  repo: "Reposition",
  mx: "Mx",
};

export type GanttWindow = {
  range: GanttRange;
  startMs: number;
  endMs: number;
  spanMs: number;
  offset: number;
};

export type GanttTick = {
  atMs: number;
  leftPct: number;
  label: string;
  major: boolean;
};

export type GanttDutyBand = {
  id: string;
  kind: GanttDutyBandKind;
  /** Fatigue: ok → warn (yellow) → alert (red). */
  level?: DutyLevel;
  leftPct: number;
  widthPct: number;
};

/** Duty strip key: same wording on the band tooltip and the legend. */
export type GanttBandKey = "duty" | "near" | "over" | "limit" | "rest";

export const GANTT_BAND_LEGEND: readonly { key: GanttBandKey; label: string }[] = [
  { key: "duty", label: "On duty" },
  { key: "near", label: "Duty near limit" },
  { key: "over", label: "Duty over limit" },
  { key: "limit", label: "Max duty window" },
  { key: "rest", label: "Rest" },
];

export function ganttBandKey(band: Pick<GanttDutyBand, "kind" | "level">): GanttBandKey {
  if (band.kind === "limit") return "limit";
  if (band.kind === "rest") return "rest";
  if (band.level === "alert" || (band.kind === "overrun" && band.level !== "warn")) return "over";
  if (band.level === "warn") return "near";
  return "duty";
}

export function ganttBandLabel(band: Pick<GanttDutyBand, "kind" | "level">): string {
  const key = ganttBandKey(band);
  return GANTT_BAND_LEGEND.find((row) => row.key === key)?.label ?? "On duty";
}

export type GanttBar = {
  id: string;
  missionId: string;
  aircraftId: string;
  crewId?: string;
  registration: string;
  flightNumber: string;
  originIcao: string;
  destIcao: string;
  route: string;
  kind: GanttBarKind;
  status: Mission["status"];
  statusTone: TicketStatusTone;
  startMs: number;
  endMs: number;
  leftPct: number;
  widthPct: number;
  conflictKinds: ClashKind[];
  /** Worst clash on the bar; drives the single conflict marker colour. */
  conflictSeverity?: ClashSeverity;
  releasePackStatus: ReleasePackStatus;
  openSupportCount: number;
};

export type GanttLane = {
  id: string;
  kind: GanttLaneKind;
  label: string;
  sublabel: string;
  aircraftId?: string;
  crewId?: string;
  grounded?: boolean;
  dutyLevel?: DutyLevel;
  dutyCaption?: string;
  advisoryDuty?: boolean;
  conflictKinds?: ClashKind[];
  bars: GanttBar[];
  bands: GanttDutyBand[];
};

export type GanttBoard = {
  window: GanttWindow;
  ticks: GanttTick[];
  nowPct: number | null;
  tailLanes: GanttLane[];
  crewLanes: GanttLane[];
  conflictCount: number;
  /** Settings → Enforce duty clock. Overlay still paints; copy says advisory vs enforced. */
  enforceDutyClock: boolean;
};

export type GanttBoardInput = {
  missions: Mission[];
  fleet?: AircraftSpec[];
  assignments?: TripAssignment[];
  /** Company roster — every pilot becomes a crew lane (same tickets, not a second list). */
  crew?: GanttCrewRef[];
  dutyViews?: DutyView[];
  dutyLimits?: DutyLimits;
  /** Default true. Off = overlay / clashes stay visible as advisory. */
  enforceDutyClock?: boolean;
  clashes?: DispatchClash[];
  now?: Date;
  range?: GanttRange;
  offset?: number;
  /** Display clock. Stored STD/STA stay UTC. Default local (ops / home-base TZ). */
  timeDisplay?: GanttTimeDisplay;
  /** IANA zone for Local. Ignored when timeDisplay is zulu. */
  timeZone?: string;
};

export function hydrateGanttTimeDisplay(raw: unknown): GanttTimeDisplay {
  return raw === "zulu" ? "zulu" : "local";
}

/** Fit-to-window. Higher values widen the time axis; Local|Z is a separate clock. */
export const GANTT_TIME_ZOOM_MIN = 1;
export const GANTT_TIME_ZOOM_MAX = 16;
export const GANTT_TIME_ZOOM_STEP = 1;
export const DEFAULT_GANTT_TIME_ZOOM = 1;

/** Clamp a stored scale to the slider steps. Junk fails closed to 1×. */
export function hydrateGanttTimeZoom(raw: unknown): number {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : Number.NaN;
  if (!Number.isFinite(n)) return DEFAULT_GANTT_TIME_ZOOM;
  const clamped = Math.min(GANTT_TIME_ZOOM_MAX, Math.max(GANTT_TIME_ZOOM_MIN, n));
  const steps = Math.round((clamped - GANTT_TIME_ZOOM_MIN) / GANTT_TIME_ZOOM_STEP);
  const snapped = GANTT_TIME_ZOOM_MIN + steps * GANTT_TIME_ZOOM_STEP;
  return Math.round(snapped * 1000) / 1000;
}

export function formatGanttTimeZoom(zoom: number): string {
  return `${hydrateGanttTimeZoom(zoom)}×`;
}

/**
 * IANA zone for this ICAO from the airport timezone table.
 * Empty when the ICAO has no timezone — callers must not paint Halifax.
 * Omit the zone entirely to keep the historical Halifax display default.
 */
export function opsTimeZoneForIcao(icao?: string | null): string {
  return timeZoneForIcao(icao) ?? "";
}

export function ganttDisplayTimeZone(timeDisplay: GanttTimeDisplay = "local", timeZone?: string | null): string {
  if (timeDisplay === "zulu") return "UTC";
  if (timeZone == null) return HALIFAX_TZ;
  const zone = timeZone.trim();
  return zone || "UTC";
}

function weekdayInZone(date: Date, timeZone: string): number {
  const p = partsInTimeZone(date, timeZone);
  return new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), 12, 0, 0)).getUTCDay();
}

/** Add calendar days on the Halifax wall, then snap to midnight (DST-safe). */
export function addHalifaxDays(date: Date, days: number): Date {
  return addDaysInTimeZone(date, days, HALIFAX_TZ);
}

export function ganttWindow(
  now: Date,
  range: GanttRange,
  offset = 0,
  timeZone = HALIFAX_TZ,
): GanttWindow {
  const p = partsInTimeZone(now, timeZone);
  const startOfToday = wallTimeInTimeZone(timeZone, Number(p.year), Number(p.month), Number(p.day), 0, 0);
  if (range === "day") {
    const start = addDaysInTimeZone(startOfToday, offset, timeZone);
    const end = addDaysInTimeZone(start, 1, timeZone);
    return {
      range,
      startMs: start.getTime(),
      endMs: end.getTime(),
      spanMs: end.getTime() - start.getTime(),
      offset,
    };
  }
  const dow = weekdayInZone(startOfToday, timeZone);
  const mondayShift = dow === 0 ? -6 : 1 - dow;
  const weekStart = addDaysInTimeZone(startOfToday, mondayShift + offset * 7, timeZone);
  const weekEnd = addDaysInTimeZone(weekStart, 7, timeZone);
  return {
    range,
    startMs: weekStart.getTime(),
    endMs: weekEnd.getTime(),
    spanMs: weekEnd.getTime() - weekStart.getTime(),
    offset,
  };
}

export function ganttBarKind(mission: Pick<Mission, "flightType" | "isMaintenance" | "parentMissionId" | "purpose">): GanttBarKind {
  if (mission.isMaintenance) return "mx";
  if (mission.flightType === "ferry") {
    const purpose = mission.purpose ?? "";
    if (mission.parentMissionId || /repo|reposition/i.test(purpose)) return "repo";
    return "ferry";
  }
  return "charter";
}

function overlapMs(startMs: number, endMs: number, window: GanttWindow): boolean {
  return startMs < window.endMs && endMs > window.startMs;
}

function clipToWindow(startMs: number, endMs: number, window: GanttWindow): { leftPct: number; widthPct: number } | null {
  if (!overlapMs(startMs, endMs, window) || window.spanMs <= 0) return null;
  const left = ((startMs - window.startMs) / window.spanMs) * 100;
  const right = ((endMs - window.startMs) / window.spanMs) * 100;
  const clippedLeft = Math.max(0, left);
  const clippedRight = Math.min(100, right);
  if (clippedRight <= clippedLeft) return null;
  return { leftPct: clippedLeft, widthPct: Math.max(clippedRight - clippedLeft, 0.55) };
}

function parseMs(iso: string | undefined): number | null {
  if (!iso) return null;
  const date = coerceClockDate(iso);
  return date ? date.getTime() : null;
}

export function ganttTicks(
  window: GanttWindow,
  timeDisplay: GanttTimeDisplay = "local",
  timeZone = HALIFAX_TZ,
): GanttTick[] {
  const zone = ganttDisplayTimeZone(timeDisplay, timeZone);
  const zulu = timeDisplay === "zulu";
  const ticks: GanttTick[] = [];
  if (window.range === "day") {
    for (let t = window.startMs; t < window.endMs; t += 2 * 3_600_000) {
      const at = new Date(t);
      const hour = partsInTimeZone(at, zone).hour;
      ticks.push({
        atMs: t,
        leftPct: ((t - window.startMs) / window.spanMs) * 100,
        label: zulu ? `${hour}:00Z` : `${hour}:00 ${zoneAbbr(at, zone)}`,
        major: hour === "00" || hour === "06" || hour === "12" || hour === "18",
      });
    }
    return ticks;
  }
  let cursor = new Date(window.startMs);
  while (cursor.getTime() < window.endMs) {
    const t = cursor.getTime();
    ticks.push({
      atMs: t,
      leftPct: ((t - window.startMs) / window.spanMs) * 100,
      label: formatCardDateInZone(cursor, zone),
      major: true,
    });
    cursor = addDaysInTimeZone(cursor, 1, zone);
  }
  return ticks;
}

export function ganttNowPct(window: GanttWindow, now: Date): number | null {
  const t = now.getTime();
  if (!Number.isFinite(t) || t < window.startMs || t > window.endMs || window.spanMs <= 0) return null;
  return ((t - window.startMs) / window.spanMs) * 100;
}

export function isAirframeDown(spec?: Pick<AircraftSpec, "status" | "available"> | null): boolean {
  if (!spec) return false;
  return spec.status === "grounded" || spec.status === "in_mx" || spec.available === false;
}

export function missionToGanttBar(
  mission: Mission,
  window: GanttWindow,
  clashes: DispatchClash[],
  crewId?: string,
  assignment?: Pick<TripAssignment, "status"> | null,
  airframeDown = false,
): GanttBar | null {
  if (!coerceClockDate(mission.departureIso)) return null;
  const startMs = missionStdMs(mission);
  const endMs = missionStaMs(mission);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
  const clip = clipToWindow(startMs, endMs, window);
  if (!clip) return null;
  const mine = clashesForMission(clashes, mission.id);
  const kinds = [...new Set(mine.map((c) => c.kind))];
  let severity: ClashSeverity | undefined = mine.some((c) => c.severity === "red") ? "red" : mine.length ? "amber" : undefined;
  if (mission.loadFlags?.some((flag) => flag === "MTOW" || flag === "MLW" || flag === "MZFW" || flag === "FUEL")) {
    kinds.push("weight");
    severity = "red";
  }
  const life = ticketLifecycle(mission, assignment);
  const tone = life === "cancelled" ? "cancelled" : airframeDown ? "grounded" : ticketStatusTone(life);
  return {
    id: mission.id,
    missionId: mission.id,
    aircraftId: mission.aircraftId ?? "",
    crewId,
    registration: mission.registration,
    flightNumber: displayFlightNumber(mission) || mission.callsign,
    originIcao: mission.originIcao,
    destIcao: mission.destIcao,
    route: `${mission.originIcao}→${mission.destIcao}`,
    kind: ganttBarKind(mission),
    status: mission.status,
    statusTone: tone,
    startMs,
    endMs,
    leftPct: clip.leftPct,
    widthPct: clip.widthPct,
    conflictKinds: kinds,
    conflictSeverity: severity,
    releasePackStatus: releasePackStatusOf(mission),
    openSupportCount: openTripSupportCount(mission),
  };
}

function worseDutyLevel(a: DutyLevel, b: DutyLevel): DutyLevel {
  if (a === "alert" || b === "alert") return "alert";
  if (a === "warn" || b === "warn") return "warn";
  return "ok";
}

function ratioDutyLevel(used: number, limit: number, warnRatio: number): DutyLevel {
  if (limit <= 0) return "ok";
  if (used >= limit) return "alert";
  if (used >= limit * warnRatio) return "warn";
  return "ok";
}

function pushBand(
  bands: GanttDutyBand[],
  id: string,
  kind: GanttDutyBandKind,
  startMs: number,
  endMs: number,
  window: GanttWindow,
  level?: DutyLevel,
): void {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return;
  let end = endMs;
  if (end <= startMs) end = startMs + 60_000;
  const clip = clipToWindow(startMs, end, window);
  if (clip) bands.push({ id, kind, level, ...clip });
}

function plannedDutyStartMs(view: DutyView | undefined, bars: GanttBar[]): number | null {
  const session = view?.session;
  const restOnly = Boolean(
    session && session.restStartedAtUtc && session.restStartedAtUtc === session.startedAtUtc,
  );
  if (session && !restOnly) {
    const start = parseMs(session.startedAtUtc);
    if (start != null) return start;
  }
  if (!bars.length) return null;
  return Math.min(...bars.map((bar) => bar.startMs));
}

function plannedDutyEndMs(view: DutyView | undefined, bars: GanttBar[], nowMs: number): number | null {
  const session = view?.session;
  if (session && view?.status === "on") return nowMs;
  if (session && view?.status === "rest") {
    return parseMs(session.endedAtUtc) ?? nowMs;
  }
  if (session?.endedAtUtc) {
    const ended = parseMs(session.endedAtUtc);
    if (ended != null) return ended;
  }
  if (!bars.length) return null;
  return Math.max(...bars.map((bar) => bar.endMs));
}

/** Live duty/rest plus FTL window / overrun so yellow-then-red is obvious on the lane. */
export function dutyBandsFor(
  view: DutyView | undefined,
  bars: GanttBar[],
  window: GanttWindow,
  nowMs: number,
  limits: DutyLimits = view?.limits ?? DEFAULT_DUTY_LIMITS,
): GanttDutyBand[] {
  const bands: GanttDutyBand[] = [];
  const session = view?.session;
  const dutyStart = plannedDutyStartMs(view, bars);
  const dutyEnd = plannedDutyEndMs(view, bars, nowMs);
  const lastBarEnd = bars.length ? Math.max(...bars.map((bar) => bar.endMs)) : null;
  const spanEnd = Math.max(dutyEnd ?? 0, lastBarEnd ?? 0, nowMs);
  const usedMin =
    dutyStart != null && dutyEnd != null ? Math.max(0, Math.round((dutyEnd - dutyStart) / 60_000)) : 0;
  const liveLevel = view?.level ?? ratioDutyLevel(usedMin, limits.maxFdpMin, limits.warnRatio);

  if (dutyStart != null) {
    const limitEnd = dutyStart + limits.maxFdpMin * 60_000;
    const warnEnd = dutyStart + Math.round(limits.maxFdpMin * limits.warnRatio) * 60_000;
    pushBand(bands, `${view?.crewId ?? "crew"}-limit`, "limit", dutyStart, limitEnd, window, "ok");
    if (dutyEnd != null) {
      pushBand(
        bands,
        `${session?.id ?? view?.crewId ?? "crew"}-duty`,
        "duty",
        dutyStart,
        dutyEnd,
        window,
        liveLevel,
      );
    }
    const overrunFrom = Math.max(limitEnd, dutyStart);
    const overrunTo = Math.max(spanEnd, lastBarEnd ?? 0);
    if (overrunTo > overrunFrom) {
      pushBand(
        bands,
        `${view?.crewId ?? "crew"}-overrun`,
        "overrun",
        overrunFrom,
        overrunTo,
        window,
        "alert",
      );
    } else if (dutyEnd != null && dutyEnd > warnEnd && dutyEnd <= limitEnd) {
      pushBand(bands, `${view?.crewId ?? "crew"}-warn`, "overrun", warnEnd, dutyEnd, window, "warn");
    }
  }

  const restStart = parseMs(session?.restStartedAtUtc);
  let restEnd = parseMs(session?.restEndedAtUtc) ?? (view?.status === "rest" ? nowMs : null);
  if (restStart == null && dutyEnd != null && session && view?.status === "off") {
    const plannedRestEnd = dutyEnd + limits.minRestMin * 60_000;
    pushBand(bands, `${view?.crewId ?? "crew"}-rest-need`, "rest", dutyEnd, plannedRestEnd, window, "ok");
  }
  if (restStart != null && restEnd != null && restEnd <= restStart) restEnd = restStart + 60_000;
  if (restStart != null && restEnd != null) {
    const restUsed = Math.round((restEnd - restStart) / 60_000);
    const restLevel =
      view?.status === "rest" && restUsed < limits.minRestMin
        ? restUsed >= limits.minRestMin * limits.warnRatio
          ? "warn"
          : "ok"
        : "ok";
    pushBand(bands, `${session?.id ?? view?.crewId ?? "crew"}-rest`, "rest", restStart, restEnd, window, restLevel);
  }
  return bands;
}

function laneDutyCaption(view: DutyView | undefined, bars: GanttBar[], advisory: boolean): string {
  const parts: string[] = [];
  if (view) {
    parts.push(view.statusLabel);
    if (view.status === "on") {
      parts.push(`${formatDutyHours(view.fdpElapsedMin)}/${formatDutyHours(view.limits.maxFdpMin)}`);
      if (view.level === "warn") parts.push("warn");
      if (view.level === "alert") parts.push("over");
    }
  } else if (bars.length) {
    parts.push("Assigned");
  } else {
    parts.push("OFF DUTY");
  }
  if (advisory) parts.push("advisory");
  return parts.join(" · ");
}

function crewLaneConflicts(bars: GanttBar[]): ClashKind[] {
  return [...new Set(bars.flatMap((bar) => bar.conflictKinds))];
}

function windowCaption(window: GanttWindow, timeZone = HALIFAX_TZ): string {
  const start = new Date(window.startMs);
  const last = new Date(window.endMs - 1);
  if (window.range === "day") return formatCardDateInZone(start, timeZone);
  return `${formatCardDateInZone(start, timeZone)} – ${formatCardDateInZone(last, timeZone)}`;
}

export function ganttWindowCaption(window: GanttWindow, timeZone = HALIFAX_TZ): string {
  return windowCaption(window, timeZone);
}

export function buildGanttBoard(input: GanttBoardInput): GanttBoard {
  const now = input.now ?? new Date();
  const range = input.range ?? "day";
  const offset = input.offset ?? 0;
  const timeDisplay = hydrateGanttTimeDisplay(input.timeDisplay);
  const timeZone = ganttDisplayTimeZone(timeDisplay, input.timeZone);
  const window = ganttWindow(now, range, offset, timeZone);
  const clashes = input.clashes ?? [];
  const assignments = input.assignments ?? [];
  const fleet = input.fleet ?? [];
  const bars: GanttBar[] = [];
  const fleetById = new Map(fleet.map((ac) => [ac.id, ac]));
  for (const mission of input.missions) {
    const assigned = assignmentForMission(assignments, mission.id);
    const spec = mission.aircraftId ? fleetById.get(mission.aircraftId) : undefined;
    const bar = missionToGanttBar(
      mission,
      window,
      clashes,
      assigned?.crewId,
      assigned,
      isAirframeDown(spec),
    );
    if (bar) bars.push(bar);
  }

  const barsByTail = new Map<string, GanttBar[]>();
  const unassigned: GanttBar[] = [];
  const fleetIds = new Set(fleet.map((ac) => ac.id));
  for (const bar of bars) {
    if (bar.aircraftId && fleetIds.has(bar.aircraftId)) {
      const list = barsByTail.get(bar.aircraftId) ?? [];
      list.push(bar);
      barsByTail.set(bar.aircraftId, list);
    } else if (bar.aircraftId) {
      const list = barsByTail.get(bar.aircraftId) ?? [];
      list.push(bar);
      barsByTail.set(bar.aircraftId, list);
    } else {
      unassigned.push(bar);
    }
  }

  const tailLanes: GanttLane[] = [];
  const seen = new Set<string>();
  for (const spec of fleet) {
    seen.add(spec.id);
    tailLanes.push({
      id: `tail:${spec.id}`,
      kind: "tail",
      label: spec.registration,
      sublabel: spec.icaoType,
      aircraftId: spec.id,
      grounded: isAirframeDown(spec),
      bars: barsByTail.get(spec.id) ?? [],
      bands: [],
    });
  }
  for (const [aircraftId, list] of barsByTail) {
    if (seen.has(aircraftId)) continue;
    tailLanes.push({
      id: `tail:${aircraftId}`,
      kind: "tail",
      label: list[0]?.registration || aircraftId,
      sublabel: "off book",
      aircraftId,
      bars: list,
      bands: [],
    });
  }
  if (unassigned.length) {
    tailLanes.push({
      id: "tail:unassigned",
      kind: "tail",
      label: "Unassigned",
      sublabel: "no tail",
      bars: unassigned,
      bands: [],
    });
  }

  const dutyViews = input.dutyViews ?? [];
  const dutyByCrew = new Map(dutyViews.map((view) => [view.crewId, view]));
  const limits = input.dutyLimits ?? dutyViews[0]?.limits ?? DEFAULT_DUTY_LIMITS;
  const enforceDutyClock = input.enforceDutyClock !== false;
  const roster = input.crew ?? [];
  const crewLanes: GanttLane[] = [];
  const crewSeen = new Set<string>();

  const pushCrewLane = (crewId: string, name: string, forceEmpty: boolean) => {
    if (crewSeen.has(crewId)) return;
    const view = dutyByCrew.get(crewId);
    const crewBars = bars.filter((b) => b.crewId === crewId);
    const bands = dutyBandsFor(view, crewBars, window, now.getTime(), view?.limits ?? limits);
    if (!forceEmpty && !crewBars.length && !bands.length) return;
    crewSeen.add(crewId);
    const aog = crewBars.some((bar) => bar.statusTone === "grounded");
    const conflicts = crewLaneConflicts(crewBars);
    const dutyLevel = bands.reduce<DutyLevel>(
      (level, band) => worseDutyLevel(level, band.level ?? "ok"),
      view?.level ?? "ok",
    );
    crewLanes.push({
      id: `crew:${crewId}`,
      kind: "crew",
      label: view?.crewName || name,
      sublabel: laneDutyCaption(view, crewBars, !enforceDutyClock),
      crewId,
      grounded: aog,
      dutyLevel,
      dutyCaption: laneDutyCaption(view, crewBars, !enforceDutyClock),
      advisoryDuty: !enforceDutyClock,
      conflictKinds: conflicts,
      bars: crewBars,
      bands,
    });
  };

  for (const member of roster) {
    if (!member.id) continue;
    pushCrewLane(member.id, member.name, true);
  }
  for (const view of dutyViews) {
    pushCrewLane(view.crewId, view.crewName, false);
  }
  for (const bar of bars) {
    if (!bar.crewId) continue;
    const assignment = assignments.find((a) => a.crewId === bar.crewId);
    pushCrewLane(bar.crewId, assignment?.crewName || bar.crewId, false);
  }

  const uncrewed = bars.filter((bar) => !bar.crewId);
  if (uncrewed.length && (roster.length || dutyViews.length || crewLanes.length)) {
    crewLanes.push({
      id: "crew:unassigned",
      kind: "crew",
      label: "Unassigned",
      sublabel: "no crew",
      bars: uncrewed,
      bands: [],
      grounded: uncrewed.some((bar) => bar.statusTone === "grounded"),
      conflictKinds: crewLaneConflicts(uncrewed),
    });
  }

  const conflictIds = new Set<string>();
  for (const bar of bars) {
    if (bar.conflictKinds.length) conflictIds.add(bar.missionId);
  }

  return {
    window,
    ticks: ganttTicks(window, timeDisplay, timeZone),
    nowPct: ganttNowPct(window, now),
    tailLanes,
    crewLanes,
    conflictCount: conflictIds.size,
    enforceDutyClock,
  };
}

export function formatGanttBarTimes(
  bar: Pick<GanttBar, "startMs" | "endMs">,
  timeDisplay: GanttTimeDisplay = "zulu",
  timeZone = HALIFAX_TZ,
): string {
  const zone = ganttDisplayTimeZone(timeDisplay, timeZone);
  if (timeDisplay === "zulu") {
    return `${formatUtcHm(bar.startMs)}–${formatUtcHm(bar.endMs)}`;
  }
  return `${formatHmInZone(bar.startMs, zone)}–${formatHmInZone(bar.endMs, zone)}`;
}
